<template>
  <div class="create-connector-wizard">
    <b-card>
      <b-row class="mb-4">
        <b-col>
          <h4>{{ $translate('create_connector') }}</h4>
          <div class="d-flex align-items-center mt-2">
            <b-badge
              v-for="(s, i) in steps"
              :key="i"
              :variant="currentStep === i ? 'primary' : (currentStep > i ? 'success' : 'secondary')"
              pill
              class="mr-2 px-3 py-2"
            >
              {{ i + 1 }}. {{ s }}
            </b-badge>
          </div>
        </b-col>
      </b-row>

      <!-- Step 1: Select Template -->
      <div v-if="currentStep === 0">
        <h5 class="mb-3">{{ $translate('select_template') }}</h5>
        <b-row>
          <b-col
            v-for="template in templates"
            :key="template.id || template.slug"
            md="4"
            class="mb-3"
          >
            <b-card
              :border-variant="selectedTemplate && selectedTemplate.slug === template.slug ? 'primary' : ''"
              :class="{ 'border-primary shadow': selectedTemplate && selectedTemplate.slug === template.slug }"
              class="cursor-pointer h-100"
              @click="selectTemplate(template)"
            >
              <h5>{{ template.name }}</h5>
              <p class="text-muted mb-1">{{ template.description }}</p>
              <b-badge variant="light">{{ template.platform_type }}</b-badge>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- Step 2: Configuration -->
      <div v-if="currentStep === 1">
        <h5 class="mb-3">{{ $translate('configuration') }}</h5>
        <b-form>
          <b-row>
            <b-col md="6">
              <b-form-group :label="$translate('name') + ' *'">
                <b-form-input v-model="form.name" required />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label="$translate('connection_mode')">
                <b-form-select v-model="form.connection_mode" :options="connectionModes" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group :label="$translate('description')">
                <b-form-textarea v-model="form.description" rows="2" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label="$translate('base_url')">
                <b-form-input v-model="form.base_url" placeholder="https://api.example.com" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label="$translate('webhook_url')">
                <b-form-input v-model="form.webhook_url" placeholder="https://your-domain.com/webhook" />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$translate('rate_limit_per_minute')">
                <b-form-input v-model.number="form.rate_limit_per_minute" type="number" min="1" />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$translate('rate_limit_per_hour')">
                <b-form-input v-model.number="form.rate_limit_per_hour" type="number" min="1" />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$translate('rate_limit_per_day')">
                <b-form-input v-model.number="form.rate_limit_per_day" type="number" min="1" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </div>

      <!-- Step 3: Authentication -->
      <div v-if="currentStep === 2">
        <h5 class="mb-3">{{ $translate('authentication') }}</h5>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$translate('auth_type')">
              <b-form-select v-model="form.auth_type" :options="authTypes" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-alert show variant="info">
          {{ $translate('credentials_generated_after_creation') }}
        </b-alert>
        <div v-if="createdCredentials">
          <b-alert show variant="warning">
            <strong>{{ $translate('save_credentials_warning') }}</strong>
          </b-alert>
          <b-row>
            <b-col md="6">
              <b-form-group label="API Key">
                <b-input-group>
                  <b-form-input :value="createdCredentials.api_key" readonly />
                  <b-input-group-append>
                    <b-button variant="outline-secondary" @click="copyToClipboard(createdCredentials.api_key)">
                      <i class="fa fa-copy" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="API Secret">
                <b-input-group>
                  <b-form-input :value="createdCredentials.api_secret" readonly />
                  <b-input-group-append>
                    <b-button variant="outline-secondary" @click="copyToClipboard(createdCredentials.api_secret)">
                      <i class="fa fa-copy" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Navigation -->
      <b-row class="mt-4">
        <b-col>
          <b-button variant="secondary" @click="goBack" :disabled="submitting">
            {{ currentStep === 0 ? $translate('cancel') : $translate('back') }}
          </b-button>
        </b-col>
        <b-col class="text-right">
          <b-button
            v-if="currentStep < 2"
            variant="primary"
            :disabled="!canAdvance"
            @click="nextStep"
          >
            {{ $translate('next') }}
          </b-button>
          <b-button
            v-else-if="!createdCredentials"
            variant="success"
            :disabled="submitting"
            @click="createConnector"
          >
            <b-spinner v-if="submitting" small class="mr-1" />
            {{ $translate('create') }}
          </b-button>
          <b-button
            v-else
            variant="primary"
            @click="goToDetail"
          >
            {{ $translate('view_connector') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      currentStep: 0,
      steps: ['Template', 'Configure', 'Credentials'],
      selectedTemplate: null,
      submitting: false,
      createdConnector: null,
      createdCredentials: null,
      form: {
        name: '',
        description: '',
        template_slug: '',
        connection_mode: 'api',
        base_url: '',
        webhook_url: '',
        auth_type: 'api_key',
        rate_limit_per_minute: 60,
        rate_limit_per_hour: 1000,
        rate_limit_per_day: 10000,
        settings: {},
      },
      connectionModes: [
        { value: 'api', text: 'API' },
        { value: 'webhook', text: 'Webhook' },
        { value: 'hybrid', text: 'Hybrid' },
      ],
      authTypes: [
        { value: 'api_key', text: 'API Key' },
        { value: 'oauth2', text: 'OAuth 2.0' },
        { value: 'basic', text: 'Basic Auth' },
      ],
    }
  },
  computed: {
    templates() {
      return this.$store.state.appConnector.templates
    },
    canAdvance() {
      if (this.currentStep === 0) return !!this.selectedTemplate
      if (this.currentStep === 1) return !!this.form.name
      return true
    },
  },
  async mounted() {
    await this.$store.dispatch('appConnector/fetchTemplates')
  },
  methods: {
    selectTemplate(template) {
      this.selectedTemplate = template
      this.form.template_slug = template.slug
      this.form.name = template.name
      if (template.default_settings) {
        this.form.base_url = template.default_settings.base_url || ''
        this.form.connection_mode = template.default_settings.connection_mode || 'api'
        this.form.auth_type = template.default_settings.auth_type || 'api_key'
        this.form.settings = { ...template.default_settings }
      }
    },
    nextStep() {
      if (this.canAdvance && this.currentStep < 2) {
        this.currentStep++
      }
    },
    goBack() {
      if (this.currentStep > 0) {
        this.currentStep--
      } else {
        this.$router.push({ name: 'appConnectors' })
      }
    },
    async createConnector() {
      this.submitting = true
      try {
        const data = await this.$store.dispatch('appConnector/create', this.form)
        this.createdConnector = data
        this.createdCredentials = data.credentials || data
      } catch (err) {
        // errorHandler already called in store
      } finally {
        this.submitting = false
      }
    },
    goToDetail() {
      if (this.createdConnector) {
        this.$router.push({ name: 'appConnectorDetail', params: { id: this.createdConnector.id } })
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
    },
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
